import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/Toast';
import { Button, Input, Textarea, Select, Card, Badge } from '../components/ui';
import { ConfirmModal } from '../components/ui/Modal';

// Opzioni per select
const STATO_OPTIONS = [
  { value: 100000000, label: 'Pianificato' },
  { value: 100000001, label: 'In scadenza' },
  { value: 100000002, label: 'Scaduto' },
  { value: 100000003, label: 'Eseguito' },
  { value: 100000004, label: 'Annullato' }
];

const PRIORITA_OPTIONS = [
  { value: 100000000, label: 'Bassa' },
  { value: 100000001, label: 'Media' },
  { value: 100000002, label: 'Alta' },
  { value: 100000003, label: 'Urgente' }
];

const statoLabels = {
  100000000: { label: 'Pianificato', variant: 'info' },
  100000001: { label: 'In scadenza', variant: 'warning' },
  100000002: { label: 'Scaduto', variant: 'danger' },
  100000003: { label: 'Eseguito', variant: 'success' },
  100000004: { label: 'Annullato', variant: 'default' }
};

const prioritaLabels = {
  100000000: { label: 'Bassa', variant: 'default' },
  100000001: { label: 'Media', variant: 'info' },
  100000002: { label: 'Alta', variant: 'warning' },
  100000003: { label: 'Urgente', variant: 'danger' }
};

function DettaglioScadenzaPage() {
  const { id } = useParams();
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [scadenza, setScadenza] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [errors, setErrors] = useState({});

  // Dati per select
  const [fornitori, setFornitori] = useState([]);
  const [rsaList, setRsaList] = useState([]);
  const [tipologieIntervento, setTipologieIntervento] = useState([]);
  const [contratti, setContratti] = useState([]);

  // Tipologie associate alla scadenza (N:N)
  const [tipologieAssociate, setTipologieAssociate] = useState([]);

  // Form data
  const [formData, setFormData] = useState({});

  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Check permissions
  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const canEdit = isAdmin || isRspp;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [scadenzaRes, fornitoriRes, rsaRes, tipologieRes, contrattiRes] = await Promise.all([
          apiRequest(`/scadenze/${id}`),
          apiRequest('/fornitori'),
          apiRequest('/rsa'),
          apiRequest('/tipologie-intervento'),
          apiRequest('/contratti')
        ]);

        setScadenza(scadenzaRes);
        setFornitori(fornitoriRes.value || []);
        setRsaList(rsaRes.value || []);
        setTipologieIntervento(tipologieRes.value || []);
        setContratti(contrattiRes.value || []);

        // Carica tipologie associate via N:N
        try {
          const tipRes = await apiRequest(`/scadenze/${id}/tipologie`);
          setTipologieAssociate(tipRes.value || []);
        } catch (tipError) {
          console.log('Tipologie N:N non disponibili:', tipError.message);
          // Fallback: usa tipologia singola dal lookup
          if (scadenzaRes.tipologiaIntervento) {
            setTipologieAssociate([{ nome: scadenzaRes.tipologiaIntervento }]);
          }
        }

        // Initialize form data
        setFormData({
          nome: scadenzaRes.nome || '',
          rsaId: scadenzaRes.rsa?.id || '',
          fornitoreId: scadenzaRes.fornitore?.id || '',
          tipologiaInterventoId: scadenzaRes.tipologiaInterventoId || '',
          contrattoId: scadenzaRes.contrattoId || '',
          dataScadenza: scadenzaRes.dataScadenza?.split('T')[0] || '',
          dataNotifica: scadenzaRes.dataNotifica?.split('T')[0] || '',
          stato: scadenzaRes.stato || 100000000,
          priorita: scadenzaRes.priorita || 100000001,
          note: scadenzaRes.note || ''
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        showToast('Errore nel caricamento della scadenza', 'error');
        navigate('/scadenze');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, apiRequest, navigate, showToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Il nome della scadenza e obbligatorio';
    }
    if (!formData.rsaId) {
      newErrors.rsaId = 'Seleziona una RSA';
    }
    if (!formData.fornitoreId) {
      newErrors.fornitoreId = 'Seleziona un fornitore';
    }
    if (!formData.dataScadenza) {
      newErrors.dataScadenza = 'La data di scadenza e obbligatoria';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      showToast('Correggi gli errori nel form', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome: formData.nome.trim(),
        fornitoreId: formData.fornitoreId,
        dataScadenza: formData.dataScadenza,
        dataNotifica: formData.dataNotifica || null,
        stato: parseInt(formData.stato),
        priorita: parseInt(formData.priorita),
        note: formData.note.trim() || null
      };

      await apiRequest(`/scadenze/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      // Reload scadenza
      const updated = await apiRequest(`/scadenze/${id}`);
      setScadenza(updated);

      showToast('Scadenza aggiornata con successo', 'success');
      setIsEditMode(false);
    } catch (error) {
      console.error('Error updating scadenza:', error);
      showToast(error.message || 'Errore nell\'aggiornamento', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeStato = async (nuovoStato) => {
    try {
      await apiRequest(`/scadenze/${id}/stato`, {
        method: 'PATCH',
        body: JSON.stringify({ stato: nuovoStato })
      });

      const updated = await apiRequest(`/scadenze/${id}`);
      setScadenza(updated);
      setFormData(prev => ({ ...prev, stato: nuovoStato }));

      showToast('Stato aggiornato', 'success');
    } catch (error) {
      console.error('Error updating stato:', error);
      showToast(error.message || 'Errore nell\'aggiornamento stato', 'error');
    }
  };

  const cancelEdit = () => {
    setFormData({
      nome: scadenza.nome || '',
      rsaId: scadenza.rsa?.id || '',
      fornitoreId: scadenza.fornitore?.id || '',
      tipologiaInterventoId: scadenza.tipologiaInterventoId || '',
      contrattoId: scadenza.contrattoId || '',
      dataScadenza: scadenza.dataScadenza?.split('T')[0] || '',
      dataNotifica: scadenza.dataNotifica?.split('T')[0] || '',
      stato: scadenza.stato || 100000000,
      priorita: scadenza.priorita || 100000001,
      note: scadenza.note || ''
    });
    setErrors({});
    setIsEditMode(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  // Calcola giorni alla scadenza
  const getDaysToDeadline = () => {
    if (!scadenza?.dataScadenza) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadline = new Date(scadenza.dataScadenza);
    deadline.setHours(0, 0, 0, 0);
    const diff = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const daysToDeadline = getDaysToDeadline();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  if (!scadenza) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Scadenza non trovata</p>
        <Button onClick={() => navigate('/scadenze')} className="mt-4">
          Torna alla lista
        </Button>
      </div>
    );
  }

  // VIEW MODE
  if (!isEditMode) {
    return (
      <div>
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">{scadenza.nome}</h1>
              <Badge variant={statoLabels[scadenza.stato]?.variant || 'default'} dot>
                {statoLabels[scadenza.stato]?.label || 'N/D'}
              </Badge>
              <Badge variant={prioritaLabels[scadenza.priorita]?.variant || 'default'}>
                {prioritaLabels[scadenza.priorita]?.label || 'N/D'}
              </Badge>
            </div>
            {daysToDeadline !== null && (
              <p className={`text-sm ${daysToDeadline < 0 ? 'text-red-600' : daysToDeadline <= 7 ? 'text-yellow-600' : 'text-gray-600'}`}>
                {daysToDeadline < 0
                  ? `Scaduta da ${Math.abs(daysToDeadline)} giorni`
                  : daysToDeadline === 0
                    ? 'Scade oggi'
                    : `Scade tra ${daysToDeadline} giorni`
                }
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/scadenze')}>
              Indietro
            </Button>
            {canEdit && (
              <Button onClick={() => setIsEditMode(true)}>
                Modifica
              </Button>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        {canEdit && scadenza.stato !== 100000003 && scadenza.stato !== 100000004 && (
          <Card className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Azioni Rapide</h2>
            <div className="flex gap-3 flex-wrap">
              <Link to={`/interventi/nuovo?scadenzaId=${scadenza.id}&rsaId=${scadenza.rsa?.id}&tipologiaId=${scadenza.tipologiaInterventoId || ''}`}>
                <Button variant="primary">
                  Registra Intervento
                </Button>
              </Link>

              {scadenza.stato !== 100000003 && (
                <Button
                  variant="success"
                  onClick={() => handleChangeStato(100000003)}
                >
                  Marca come Eseguito
                </Button>
              )}

              {scadenza.stato !== 100000004 && (
                <Button
                  variant="outline"
                  onClick={() => handleChangeStato(100000004)}
                >
                  Annulla Scadenza
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Info Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Informazioni Base */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informazioni Base</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">RSA</dt>
                <dd className="font-medium">{scadenza.rsa?.nome || '-'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Fornitore</dt>
                <dd className="font-medium">{scadenza.fornitore?.nome || '-'}</dd>
              </div>
              <div>
                <dt className="text-gray-500 mb-1">Tipologie Intervento</dt>
                <dd className="font-medium">
                  {tipologieAssociate.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {tipologieAssociate.map((tip, idx) => (
                        <Badge key={tip.id || idx} variant="info">
                          {tip.nome}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    scadenza.tipologiaIntervento || '-'
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Contratto</dt>
                <dd className="font-medium">{scadenza.contratto || '-'}</dd>
              </div>
            </dl>
          </Card>

          {/* Date */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Date</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">Data Scadenza</dt>
                <dd className="font-medium">{formatDate(scadenza.dataScadenza)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Data Notifica</dt>
                <dd className="font-medium">{formatDate(scadenza.dataNotifica)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Ultimo Intervento</dt>
                <dd className="font-medium">{formatDate(scadenza.dataUltimoIntervento)}</dd>
              </div>
            </dl>
          </Card>

          {/* Notifiche */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Stato Notifiche</h2>
            <dl className="space-y-3">
              <div className="flex justify-between items-center">
                <dt className="text-gray-500">Notifica 30 giorni</dt>
                <dd>
                  <Badge variant={scadenza.notifica30Inviata ? 'success' : 'default'}>
                    {scadenza.notifica30Inviata ? 'Inviata' : 'Non inviata'}
                  </Badge>
                </dd>
              </div>
              <div className="flex justify-between items-center">
                <dt className="text-gray-500">Notifica 7 giorni</dt>
                <dd>
                  <Badge variant={scadenza.notifica7Inviata ? 'success' : 'default'}>
                    {scadenza.notifica7Inviata ? 'Inviata' : 'Non inviata'}
                  </Badge>
                </dd>
              </div>
              <div className="flex justify-between items-center">
                <dt className="text-gray-500">Notifica scaduto</dt>
                <dd>
                  <Badge variant={scadenza.notificaScadutoInviata ? 'danger' : 'default'}>
                    {scadenza.notificaScadutoInviata ? 'Inviata' : 'Non inviata'}
                  </Badge>
                </dd>
              </div>
            </dl>
          </Card>
        </div>

        {/* Note */}
        {scadenza.note && (
          <Card className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Note</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{scadenza.note}</p>
          </Card>
        )}
      </div>
    );
  }

  // EDIT MODE
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Modifica Scadenza</h1>
          <p className="text-gray-600">{scadenza.nome}</p>
        </div>
        <Button variant="outline" onClick={cancelEdit}>
          Annulla
        </Button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        {/* Informazioni Base */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Informazioni Base</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome Scadenza *"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              error={errors.nome}
            />

            <Select
              label="Fornitore *"
              name="fornitoreId"
              value={formData.fornitoreId}
              onChange={handleChange}
              error={errors.fornitoreId}
              options={[
                { value: '', label: 'Seleziona fornitore...' },
                ...fornitori.map(f => ({ value: f.id, label: f.nome }))
              ]}
            />

            {/* RSA e Tipologia in sola lettura */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">RSA</label>
              <p className="text-gray-900 py-2">{scadenza.rsa?.nome || '-'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipologie Intervento</label>
              <div className="py-2">
                {tipologieAssociate.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {tipologieAssociate.map((tip, idx) => (
                      <Badge key={tip.id || idx} variant="info">
                        {tip.nome}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-gray-900">{scadenza.tipologiaIntervento || '-'}</span>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Date */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Date</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="date"
              label="Data Scadenza *"
              name="dataScadenza"
              value={formData.dataScadenza}
              onChange={handleChange}
              error={errors.dataScadenza}
            />

            <Input
              type="date"
              label="Data Notifica"
              name="dataNotifica"
              value={formData.dataNotifica}
              onChange={handleChange}
            />
          </div>
        </Card>

        {/* Stato e Priorita */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Stato e Priorita</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Stato"
              name="stato"
              value={formData.stato}
              onChange={handleChange}
              options={STATO_OPTIONS}
            />

            <Select
              label="Priorita"
              name="priorita"
              value={formData.priorita}
              onChange={handleChange}
              options={PRIORITA_OPTIONS}
            />
          </div>
        </Card>

        {/* Note */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Note</h2>

          <Textarea
            name="note"
            value={formData.note}
            onChange={handleChange}
            rows={4}
          />
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={cancelEdit}>
            Annulla
          </Button>
          <Button type="submit" loading={saving}>
            Salva Modifiche
          </Button>
        </div>
      </form>
    </div>
  );
}

export default DettaglioScadenzaPage;
